/**
 * Bottom sheet to create a route alert ("follow this route"). Quick by default
 * (one tap sets the alert for the exact route searched); a "More options"
 * expand reveals an optional max-price filter.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createSubscription } from '../lib/api';
import { translateError, useT } from '../lib/i18n';
import { hapticError, hapticSuccess } from '../lib/telegram';
import { BottomSheet } from './BottomSheet';
import { CardHint } from './Card';
import { TextInput } from './TextInput';
import { useToast } from './Toast';

interface Props {
  originCityId: string;
  destinationCityId: string;
  onClose: () => void;
}

export function RouteAlertSheet({ originCityId, destinationCityId, onClose }: Props): JSX.Element {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [advanced, setAdvanced] = useState(false);
  const [maxPrice, setMaxPrice] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      createSubscription({
        originCityId,
        destinationCityId,
        maxPrice: maxPrice ? Number(maxPrice) : undefined,
      }),
    onSuccess: async () => {
      hapticSuccess();
      toast(t('alert.set.toast'));
      await qc.invalidateQueries({ queryKey: ['subscriptions'] });
      onClose();
    },
    onError: (e: Error) => {
      hapticError();
      setError(translateError(t, e));
    },
  });

  return (
    <BottomSheet onClose={onClose} title={t('alert.set.title')}>
      <div className="space-y-3">
        <CardHint>{t('alert.set.body')}</CardHint>

        <button
          type="button"
          onClick={() => setAdvanced((v) => !v)}
          className="text-xs font-medium text-tg-link underline"
        >
          {t('alert.more')}
        </button>

        {advanced && (
          <TextInput
            id="alert-max-price"
            label={t('alert.maxPrice')}
            type="number"
            min={1}
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
          />
        )}

        {error && (
          <p className="rounded-input bg-destructive/10 p-2 text-xs text-destructive">{error}</p>
        )}

        <button
          type="button"
          onClick={() => mut.mutate()}
          disabled={mut.isPending}
          className="w-full rounded-input bg-brand-green py-3 text-sm font-semibold text-white disabled:opacity-50"
        >
          {mut.isPending ? '…' : t('alert.set.cta')}
        </button>
      </div>
    </BottomSheet>
  );
}
